import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivitiesParams, ActivityType } from '../types/activityTypes';
import Select from '@/components/form/Select';
import { ColumnFilter } from '@/components/ui/table/ColumnFilter';
import Button from '@/components/ui/button/Button';
import DateRangeFilter from './DateRangeFilter';

interface ActivitiesFiltersProps {
  currentFilters: Partial<ActivitiesParams>;
  onFilterChange: (filters: Partial<ActivitiesParams>) => void;
}

const ActivitiesFilters: React.FC<ActivitiesFiltersProps> = ({
  currentFilters,
  onFilterChange,
}) => {
  const { t } = useTranslation();
  const [dateRangeValue, setDateRangeValue] = useState('all');

  // Обновляем значение dateRangeValue при изменении фильтров извне
  useEffect(() => {
    if (!currentFilters.activity_start_date && !currentFilters.activity_end_date) {
      setDateRangeValue('all');
    }
  }, [currentFilters.activity_start_date, currentFilters.activity_end_date]);

  const eventTypeOptions = [
    { value: '', label: t('activities.filters.event_type.all') },
    { value: 'delivery_to_client', label: t('activities.filters.event_type.delivery') },
    { value: 'pickup_from_client', label: t('activities.filters.event_type.pickup') },
    { value: 'handoff_to_service', label: t('activities.filters.event_type.to_service') },
    { value: 'receiving_from_service', label: t('activities.filters.event_type.from_service') },
  ];

  const reportOptions = [
    { value: '', label: t('activities.filters.report.all') },
    { value: 'true', label: t('activities.filters.report.with') },
    { value: 'false', label: t('activities.filters.report.without') },
  ];

  const dateRangeOptions = [
    { value: 'today', label: t('activities.filters.date_range.today') },
    { value: 'week', label: t('activities.filters.date_range.week') },
    { value: 'month', label: t('activities.filters.date_range.month') },
    { value: 'year', label: t('activities.filters.date_range.year') },
    { value: 'custom', label: t('activities.filters.date_range.custom') },
  ];

  const handleDateRangeChange = (startDate: string | undefined, endDate: string | undefined, value: string) => {
    setDateRangeValue(value);
    onFilterChange({
      ...currentFilters,
      activity_start_date: startDate,
      activity_end_date: endDate,
    });
  };

  const handleResetFilters = () => {
    setDateRangeValue('all');
    onFilterChange({
      activity_type: undefined,
      has_report: undefined,
      activity_start_date: undefined,
      activity_end_date: undefined,
    });
  };

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters =
    currentFilters.activity_type !== undefined ||
    currentFilters.has_report !== undefined ||
    currentFilters.activity_start_date !== undefined ||
    currentFilters.activity_end_date !== undefined;

  return (
    <div className="flex gap-4 flex-wrap items-center">
      <div className="ml-0">
        <ColumnFilter
          size="sm"
          buttonText={t('filters.columns')}
          className="w-auto"
        />
      </div>
      <DateRangeFilter
        value={dateRangeValue}
        onChange={handleDateRangeChange}
      />
      <Select
        testId="filter-activity-type"
        value={currentFilters.activity_type || ''}
        onChange={(value) => onFilterChange({
          ...currentFilters,
          activity_type: (value || undefined) as ActivityType | undefined
        })}
        options={eventTypeOptions}
        className="w-full"
        placeholder={t('activities.filters.event_type.placeholder')}
      />
      <Select
        testId="filter-has-report"
        value={currentFilters.has_report?.toString() || ''}
        onChange={(value) => onFilterChange({
          ...currentFilters,
          has_report: value ? value === 'true' : undefined
        })}
        options={reportOptions}
        className="w-full"
        placeholder={t('activities.filters.report.placeholder')}
      />
      {hasActiveFilters && (
        <Button
          testId="filter-reset-button"
          variant="outline"
          size="sm"
          onClick={handleResetFilters}
          className="whitespace-nowrap flex items-center gap-1 !p-3"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t('activities.filters.reset')}
        </Button>
      )}
    </div>
  );
};

export default ActivitiesFilters;
